import { getRoomSecret, isValidRoomSecret, withRoomSecret } from "./roomCrypto";

export type InviteContact = {
  pseudo: string;
  numero: string;
};

export function buildRoomInviteUrl(roomId: string): string | null {
  if (typeof window === "undefined" || !roomId) return null;
  const secret = getRoomSecret(roomId);
  if (!isValidRoomSecret(secret)) return null;

  return new URL(
    withRoomSecret(`/room/${encodeURIComponent(roomId)}`, secret),
    window.location.origin,
  ).toString();
}

export function buildInviteText(roomId: string, personalMessage = ""): { url: string; text: string } | null {
  const url = buildRoomInviteUrl(roomId);
  if (!url) return null;
  const prefix = personalMessage.trim();
  return {
    url,
    text: prefix ? `${prefix}\n\n${url}` : `Rejoins ma room ROOM :\n${url}`,
  };
}

export function normalizeWhatsAppNumber(numero: string): string {
  const trimmed = numero.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("0") && digits.length === 10) return `33${digits.slice(1)}`;
  return digits;
}

export function getWhatsAppShareUrl(numero: string, text: string): string {
  const phone = normalizeWhatsAppNumber(numero);
  const encodedText = encodeURIComponent(text);
  return phone
    ? `https://wa.me/${phone}?text=${encodedText}`
    : `https://wa.me/?text=${encodedText}`;
}

export function getTelegramShareUrl(text: string, url: string): string {
  // Telegram's share endpoint works from the browser and can hand off
  // to Telegram Web when no native client is available.
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
}

export function getWhatsAppWebShareUrl(numero: string, text: string): string {
  const phone = normalizeWhatsAppNumber(numero);
  return phone
    ? `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(text)}`
    : `https://web.whatsapp.com/send?text=${encodeURIComponent(text)}`;
}

export function getTelegramWebUrl(): string {
  return "https://web.telegram.org/";
}

export function getMessengerWebUrl(): string {
  return "https://www.messenger.com/";
}

export function getSmsShareUrl(numero: string, text: string): string {
  return `sms:${encodeURIComponent(numero)}?body=${encodeURIComponent(text)}`;
}

export async function tryNativeRoomShare(
  roomId: string,
  personalMessage = "",
): Promise<"shared" | "cancelled" | "unavailable"> {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") return "unavailable";

  const invite = buildInviteText(roomId, personalMessage);
  if (!invite) return "unavailable";

  try {
    await navigator.share({
      title: "ROOM",
      text: personalMessage.trim() || "Rejoins ma room ROOM",
      url: invite.url,
    });
    return "shared";
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
    return "unavailable";
  }
}
